import { useCallback } from 'react';

import { useNavigate } from '@change-corgi/core-react/router';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { LoginOrJoinMethod } from '#src/app/shared/utils/loginOrJoin';
import { getLoginOrJoinMethodQueryKey, getLoginOrJoinMethodShortcode } from '#src/app/shared/utils/loginOrJoin';

import { useGetRedirectUrl } from '../useGetRedirectUrl';

type GotoRedirectToParams = {
	loginOrJoinMethod?: LoginOrJoinMethod;
};

type GotoRedirectFn = (params?: GotoRedirectToParams) => void;

export function useGotoRedirect(): GotoRedirectFn {
	const navigate = useNavigate();
	const { resolveUrl } = useUtilityContext();
	const getRedirectUrl = useGetRedirectUrl();

	const gotoRedirectTo = useCallback(
		({ loginOrJoinMethod }: GotoRedirectToParams = {}) => {
			const methodShortcode = getLoginOrJoinMethodShortcode(loginOrJoinMethod);
			const url = getRedirectUrl({
				additionalQueryParams: {
					// adds the loginOrJoin method to the url we're being redirected to as a query param
					...(methodShortcode ? { [getLoginOrJoinMethodQueryKey()]: methodShortcode } : {}),
				},
			});
			// using resolveUrl() here results in not using the react-router for navigation
			// (which is what we want since the user has just changed and we want to refresh l10n)
			navigate(resolveUrl(url));
		},
		[navigate, resolveUrl, getRedirectUrl],
	);

	return gotoRedirectTo;
}
